import type { ApolloCache } from '@apollo/client';
import { useMutation } from '@apollo/client';
import type { EraseAllToolScope } from 'generated/graphql';
import {
  GET_ME,
  SET_DEFAULT_STARTING_IMAGE,
  SET_DEFAULT_THUMBNAIL_SIZE,
  TOGGLE_ERASE_ALL_TOOL_SCOPE,
} from 'modules/Apollo/queries';
import { NameSpaces, sendCrossTabEvent } from 'modules/EventsManager';
import assocPath from 'ramda/es/assocPath';
import { notifyUserSettingsUpdate } from './notifyUserSettingsUpdate';
import { useCurrentUser } from './useCurrentUser';
import { WithApolloContext } from 'modules/Apollo/types';

/** Hook for fetching and setting the Erase All tool behavior */
export const useEraseAllToolScopeSetting = ({
  context,
}: WithApolloContext): [EraseAllToolScope | null, (arg1: EraseAllToolScope) => Promise<void>] => {
  const { data, refetch: refetchUser } = useCurrentUser({ context });
  const me = data?.me;
  const eraseAllToolScope = me?.viewerSettings?.eraseAllToolScope ?? null;

  const [saveEraseAllToolScopeValue] = useMutation(TOGGLE_ERASE_ALL_TOOL_SCOPE, {
    onCompleted: () => {
      notifyUserSettingsUpdate();
    },
    onError: () => {
      refetchUser();
    },
    update(cache: ApolloCache<unknown>, res, { variables }) {
      if (me == null) {
        return;
      }

      const { eraseAllToolScope } = variables || {
        eraseAllToolScope: null,
      };

      cache.writeQuery({
        query: GET_ME,
        data: assocPath(['me', 'viewerSettings', 'eraseAllToolScope'], eraseAllToolScope, data),
      });
    },
    context,
  });

  const toggleEraseAllToolScope = async (eraseAllToolScope: EraseAllToolScope) => {
    await saveEraseAllToolScopeValue({
      variables: {
        eraseAllToolScope,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        toggleEraseAllToolScope: {
          eraseAllToolScope,
          __typename: 'EraseAllToolScopeResponse',
        },
      },
    });
  };

  return [eraseAllToolScope, toggleEraseAllToolScope];
};

/** Hook for fetching and setting the default thumbnail size for the viewer */
export const useDefaultThumbnailSizeSetting = ({
  context,
}: WithApolloContext): [number | null, (arg1: number) => Promise<void>] => {
  const { data, refetch: refetchUser } = useCurrentUser({ context });
  const me = data?.me;
  const thumbnailSizePx = me?.viewerSettings?.defaultThumbnailSizePx ?? null;

  const [setDefaultThumbnailSizeValue] = useMutation(SET_DEFAULT_THUMBNAIL_SIZE, {
    onCompleted: () => {
      notifyUserSettingsUpdate();
    },
    onError: () => {
      refetchUser();
    },
    update(cache: ApolloCache<unknown>, res, { variables }) {
      if (me == null) {
        return;
      }
      refetchUser();
      cache.writeQuery({
        query: GET_ME,
        data: assocPath(['me', 'viewerSettings', 'defaultThumbnailSizePx'], thumbnailSizePx, data),
      });
    },
    context,
  });

  const setDefaultThumbnailSize = async (thumbnailSizePx: number) => {
    await setDefaultThumbnailSizeValue({
      variables: {
        thumbnailSizePx,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        setDefaultThumbnailSize: {
          thumbnailSizePx,
          __typename: 'DefaultThumbnailSizeResponse',
        },
      },
    });
  };

  return [thumbnailSizePx, setDefaultThumbnailSize];
};

export const useStartingImageSetting = ({
  context,
}: WithApolloContext): [string | null, (arg1: string) => Promise<void>] => {
  const { data, refetch: refetchUser } = useCurrentUser({ context });
  const me = data?.me;
  const startingImageSetting = me?.viewerSettings?.defaultStartingImage ?? null;

  const [setDefaultStartingImageValue] = useMutation(SET_DEFAULT_STARTING_IMAGE, {
    onCompleted: async () => {
      notifyUserSettingsUpdate();
      if (startingImageSetting !== null)
        sendCrossTabEvent(NameSpaces.DRE, { type: 'reset-slice', setting: startingImageSetting });
    },
    onError: () => {
      refetchUser();
    },
    update(cache, _res, { variables }) {
      const newValue = variables?.startingImage;
      if (!newValue) return;

      const existing = cache.readQuery<{ me?: any }>({ query: GET_ME });
      if (!existing?.me) return;

      cache.writeQuery({
        query: GET_ME,
        data: {
          ...existing,
          me: {
            ...existing.me,
            viewerSettings: {
              ...existing.me.viewerSettings,
              defaultStartingImage: newValue,
              __typename: existing.me.viewerSettings?.__typename ?? 'ViewerSettings',
            },
            __typename: existing.me.__typename ?? 'User',
          },
        },
      });
    },
    context,
  });

  const setDefaultStartingImage = async (startingImage: string) => {
    await setDefaultStartingImageValue({
      variables: {
        startingImage,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        setDefaultStartingImage: {
          startingImage,
          __typename: 'DefaultStartingImageResponse',
        },
      },
    });
  };

  return [startingImageSetting, setDefaultStartingImage];
};
